import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormInput } from '../FormInput/FormInput';
import './Select.scss';

export const Select = ({
  options = [],
  value,
  onChange,
  onBlur,
  placeholder = 'Select an option',
  focusVariant = 'primary',
  className = '',
  disabled = false,
  multiselect = false,
  error = false,
  showSearch = true, // New parameter to control search input visibility
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const selectRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term (only if search is enabled)
  const filteredOptions = showSearch
    ? options.filter((option) => {
        // Ensure option exists and has a label property
        if (!option || typeof option !== 'object' || !option.label) {
          return false;
        }
        return option.label.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : options; // If search is disabled, show all options

  // Get selected option label(s)
  const getSelectedLabel = () => {
    if (multiselect) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (selectedValues.length === 0) return placeholder;
      if (selectedValues.length === 1) {
        const selectedOption = options.find(
          (option) => option.value === selectedValues[0]
        );
        return selectedOption ? selectedOption.label : placeholder;
      }
      return `${selectedValues.length} selected`;
    } else {
      const selectedOption = options.find((option) => option.value === value);
      return selectedOption ? selectedOption.label : placeholder;
    }
  };

  const selectedLabel = getSelectedLabel();

  // Calculate dropdown direction based on available space
  const calculateDropdownDirection = () => {
    if (!selectRef.current) return 'down';

    const rect = selectRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 300; // Estimated dropdown height

    // If there's more space above and not enough space below, open upward
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      return 'up';
    }
    return 'down';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        const wasOpen = isOpen;
        setIsOpen(false);
        setSearchTerm('');
        // Trigger onBlur when clicking outside (only if dropdown was open)
        if (wasOpen && onBlur) {
          onBlur();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onBlur]);

  // Handle option selection
  const handleOptionClick = (optionValue) => {
    if (multiselect) {
      const selectedValues = Array.isArray(value) ? value : [];
      const isSelected = selectedValues.includes(optionValue);

      if (isSelected) {
        // Remove from selection
        const newValues = selectedValues.filter((val) => val !== optionValue);
        onChange(newValues);
      } else {
        // Add to selection
        onChange([...selectedValues, optionValue]);
      }
      // Keep dropdown open for multiselect
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
      // Trigger onBlur when option is selected
      if (onBlur) {
        onBlur();
      }
    }
  };

  // Handle dropdown toggle with direction calculation
  const handleDropdownToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        const direction = calculateDropdownDirection();
        setDropdownDirection(direction);
      }
      setIsOpen(!isOpen);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (filteredOptions.length === 1) {
        handleOptionClick(filteredOptions[0].value);
      }
    }
  };

  return (
    <div
      className={`custom-select ${className} ${isOpen ? 'open' : ''} ${
        disabled ? 'disabled' : ''
      } ${error ? 'has-error' : ''} ${
        focusVariant ? `focus-${focusVariant}` : ''
      }`}
      ref={selectRef}
    >
      <div className='select-trigger' onClick={handleDropdownToggle}>
        <span className='select-value'>{selectedLabel}</span>
        <div className={`select-arrow ${isOpen ? 'rotated' : ''}`}>
          <svg width='12' height='8' viewBox='0 0 12 8' fill='none'>
            <path
              d='M1 1.5L6 6.5L11 1.5'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div
          className={`select-dropdown ${
            dropdownDirection === 'up' ? 'dropdown-up' : 'dropdown-down'
          }`}
        >
          {showSearch && (
            <div className='search-container'>
              <FormInput
                id='select-search-input'
                type='text'
                value={searchTerm}
                onChange={(newValue) => setSearchTerm(newValue)}
                placeholder='Search options...'
                inputProps={{
                  ref: inputRef,
                  onKeyDown: handleKeyDown,
                  autoFocus: true,
                  className: 'search-input',
                }}
                focusVariant='primary-gradient'
              />
            </div>
          )}

          <div className='options-container'>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = multiselect
                  ? (Array.isArray(value) ? value : []).includes(option.value)
                  : option.value === value;

                return (
                  <div
                    key={option.value}
                    className={`select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleOptionClick(option.value)}
                  >
                    <span className='option-label'>{option.label}</span>
                    {isSelected && (
                      <div className='check-icon'>
                        <svg
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                        >
                          <path
                            d='M13.5 4.5L6 12L2.5 8.5'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className='no-options'>No options found</div>
            )}
          </div>
        </div>
      )}

      {/* Selected Options Display for Multiselect */}
      {multiselect && Array.isArray(value) && value.length > 0 && (
        <div className='selected-options'>
          <div className='selected-options-header'>
            <span>Selected ({value.length})</span>
            <button
              className='clear-all-btn'
              onClick={() => onChange([])}
              disabled={disabled}
            >
              Clear All
            </button>
          </div>
          <div className='selected-options-list'>
            {value.map((selectedValue) => {
              const option = options.find((opt) => opt.value === selectedValue);
              return option ? (
                <div key={selectedValue} className='selected-option-tag'>
                  <span>{option.label}</span>
                  <button
                    className='remove-option-btn'
                    onClick={() => {
                      const newValues = value.filter(
                        (val) => val !== selectedValue
                      );
                      onChange(newValues);
                    }}
                    disabled={disabled}
                  >
                    ×
                  </button>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  focusVariant: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  multiselect: PropTypes.bool,
  error: PropTypes.bool,
  showSearch: PropTypes.bool,
};
